import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { subscribeToGrowTargetingCache } from '@shared/services/userContentService';
import { GrowTargetingCache } from '@shared/types';
import { useLiveDataUid } from './useLiveDataUid';

/**
 * Focused slice behind the DataContext facade: the grow targeting cache —
 * eligible video/exercise IDs, computed once per UTC day server-side.
 *
 * NOTE: components should keep consuming useData() from DataContext for now —
 * demo mode (DemoProviders) only stubs the facade's DataContext.Provider, so a
 * component reading this context directly would crash under /demo.
 */
interface TargetingContextType {
    growTargetingCache: GrowTargetingCache | null;
}

export const TargetingContext = createContext<TargetingContextType | undefined>(undefined);

export const useTargetingContext = () => {
    const context = useContext(TargetingContext);
    if (!context) {
        throw new Error('useTargetingContext must be used within a TargetingProvider');
    }
    return context;
};

export const TargetingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const uid = useLiveDataUid();
    const [growTargetingCache, setGrowTargetingCache] = useState<GrowTargetingCache | null>(null);

    useEffect(() => {
        if (!uid) {
            setGrowTargetingCache(null);
            return;
        }
        return subscribeToGrowTargetingCache(uid, (cache) => {
            setGrowTargetingCache(cache);
        });
    }, [uid]);

    const value = useMemo(() => ({ growTargetingCache }), [growTargetingCache]);

    return (
        <TargetingContext.Provider value={value}>
            {children}
        </TargetingContext.Provider>
    );
};
